import { v4 as UUIDv4 } from 'uuid';
import { getGroupPosition } from './size';
import { getNextName } from './getNextName';

const imageSubGeneratorIdPrefix = {
  upscale: 'generatorUpscale',
  changeStyle: 'generatorTransfe',
  remix: 'generatorRemix',
  inpaint: 'generatorInpainting',
  outpaint: 'generatorOutpaint',
  changeFace: 'generatorChangeface',
};

export const getImageGeneratorIdOfTool = (imageId, imageTool) => {
  return imageSubGeneratorIdPrefix[imageTool] + imageId.replace('element-image', '');
};

export const findElement = (elements, id) => {
  let target = null;

  elements.some((element) => {
    if (element.id === id) return (target = element);
    if (
      element.elements?.some((child) => {
        if (child.id === id) return (target = element);
      })
    ) {
      return true;
    }
  });
  return target;
};

export const cleanElements = () => {};

// 在右侧空间查找合适的插入位置
export const findRightInsertOffset = (position, elements, gap = 10) => {
  const { x, y, width, height } = position;

  const nextElements = elements
    .filter((ele) => {
      return !(ele.y + ele.height * (ele.scaleY || 1) < y || ele.y > y + height);
    })
    .filter((ele) => {
      return !(ele.x + ele.width * (ele.scaleX || 1) + gap < x);
    })
    .sort((a, b) => a.x + a.width * (a.scaleX || 1) - (b.x + b.width * (b.scaleX || 1)));

  let targertX = x;

  let hasIntersectionElements = nextElements.some((ele) => {
    return !(targertX + width + gap <= ele.x || targertX >= ele.x + ele.width * (ele.scaleX || 1) + gap);
  });

  if (!hasIntersectionElements) {
    return targertX;
  }

  while (hasIntersectionElements) {
    const lastEle = nextElements[0];
    targertX = lastEle.x + lastEle.width * (lastEle.scaleX || 1) + gap;
    nextElements.shift();
    hasIntersectionElements = nextElements.some((ele) => {
      return !(targertX + width + gap <= ele.x || targertX >= ele.x + ele.width * (ele.scaleX || 1) + gap);
    });
  }

  return findRightInsertOffset({ x: targertX, y, width, height }, elements, gap);
};

export const generatePolygon = (x, y, r, edges = 3) => {
  const points = [];
  const delta = (2 * Math.PI) / edges;
  for (let i = 0; i < edges; i++) {
    const theta = i * delta;
    points.push([x + r * Math.sin(theta), y + r * Math.cos(theta)]);
  }
  return points;
};

export const ungroup = (group) => {
  return group.elements.map((el) => {
    const x = group.x + el.x * group.scaleX;
    const y = group.y + el.y * group.scaleY;
    const scaleX = el.scaleX * group.scaleX;
    const scaleY = el.scaleY * group.scaleY;
    return Object.assign({}, el, { x, y, scaleY, scaleX });
  });
};

export const makeGroup = (children, groupProps = {}) => {
  const position = getGroupPosition(children);
  children = children.map((el) => {
    const x = el.x - position.left;
    const y = el.y - position.top;
    return Object.assign({}, el, { x, y });
  });

  return Object.assign(
    {},
    {
      id: `elementGroup${UUIDv4()}`,
      key: `elementGroupKey${UUIDv4()}`, // group组件key不变的话，操作组内元素会造成渲染不完全更新的问题
      type: 'group',
      x: position.left,
      y: position.top,
      scaleX: 1,
      scaleY: 1,
      width: position.right - position.left,
      height: position.bottom - position.top,
      elements: children,
      ...groupProps,
    },
  );
};

export const groupElements = (ids, elements = []) => {
  const idsMap = Object.fromEntries(ids.map((i) => [i, true]));
  const targetElements = [];
  let targetIndex = -1;
  const nextElements = elements.reduce((res, element, index) => {
    if (idsMap[element.id]) {
      targetIndex = index;
      if (element.elements) {
        targetElements.push(...ungroup(element));
      } else {
        targetElements.push(element);
      }
      res.push(null);
    } else if (element.elements) {
      const children = element.elements.filter((child) => !idsMap[child.id]);
      const hitGroup = children.length < element.elements.length;
      if (hitGroup) {
        targetIndex = index;
        const preChildren = ungroup(element);
        switch (children.length) {
          case 0:
            targetElements.push(...preChildren);
            res.push(null);
            break;
          case 1:
            targetElements.push(...preChildren.filter((child) => idsMap[child.id]));
            res.push(...preChildren.filter((child) => !idsMap[child.id]));
            break;
          default:
            targetElements.push(...preChildren.filter((child) => idsMap[child.id]));
            res.push(
              makeGroup(
                preChildren.filter((child) => !idsMap[child.id]),
                {
                  name: element.name,
                  id: element.id,
                },
              ),
            );
        }
      } else {
        res.push(element);
      }
    } else {
      res.push(element);
    }
    return res;
  }, []);

  const nextGroup = makeGroup(targetElements, {
    name: getNextName(elements, 'Group'),
  });
  nextElements.splice(targetIndex, 0, nextGroup);
  return [nextElements.filter((i) => i), nextGroup];
};

export const groupElements2 = (ids, elements = []) => {
  const idMap = Object.fromEntries(ids.map((i) => [i, true]));
  let insertIndex = -1;
  const groupIdMap = {};
  let children = elements.reduce((res, element, index) => {
    if (element.type !== 'group' && idMap[element.id]) {
      res.push(element);
      groupIdMap[element.id] = true;
      if (insertIndex < 0) insertIndex = index;
    } else if (element.type === 'group') {
      const selected = element.elements.filter((ele) => idMap[ele.id]);
      if (selected.length && insertIndex < 0) insertIndex = index;
      res.push(...selected);
      element.elements = element.elements.filter((ele) => !idMap[ele.id]);
    }
    return res;
  }, []);
  const nextGroup = makeGroup(children, {
    name: getNextName(elements, 'Group'),
  });
  elements.splice(insertIndex, 0, nextGroup);
  return [
    elements.filter((element) => !idMap[element.id] && (element.type !== 'group' || element.elements?.length > 0)),
    nextGroup,
  ];
};

export const ungroupElements = (ids, elements) => {
  const idsMap = Object.fromEntries(ids.map((id) => [id, true]));
  return elements.reduce((res, element) => {
    if (idsMap[element.id] && element.elements) {
      res.push(...ungroup(element));
    } else {
      res.push(element);
    }
    return res;
  }, []);
};

export const regroupElements = (groupId, preUpdate, elements) => {
  if (typeof preUpdate === 'function') {
    elements = preUpdate(elements);
  }
  const [nextElements, group] = ungroupElements(groupId, elements);
  const ids = group.elements.map((el) => el.id);
  const [finalElements] = groupElements(ids, nextElements, {
    id: group.id,
    name: group.name,
  });
  return finalElements;
};

export const updateElement = (id, updatedElement, elements) => {
  return updateElements(elements, { [id]: updatedElement });
};

export const updateElements = (elements, elementsMap) => {
  return elements.map((element) => {
    if (elementsMap[element.id]) {
      const updatedData = elementsMap[element.id];
      const nextData = Object.assign({}, element, updatedData);

      // 如果url变了，就更新一下时间戳
      if (updatedData.url) nextData.t = Date.now();

      return Object.assign(nextData);
    } else if (element.elements) {
      let hitGroup = null;
      let needRegroup = false;
      const children = element.elements.map((child) => {
        const target = elementsMap[child.id];
        if (target) {
          hitGroup = element.id;
          needRegroup = ['x', 'y', 'scaleX', 'scaleY'].some((key) => target[key] !== undefined);
          return Object.assign({}, child, target);
        }
        return child;
      });
      const newGroup = Object.assign({}, element, { elements: children });
      if (needRegroup) {
        return regroup(newGroup);
      } else if (hitGroup) {
        return newGroup;
      }
    }
    return element;
  });
};

export const regroup = (group) => {
  const children = ungroup(group);
  return makeGroup(children, {
    id: group.id,
    name: group.name,
    collage: group.collage,
  });
};

export const deleteElements = (ids, elements) => {
  const idMap = Object.fromEntries(ids.map((id) => [id, true]));
  elements = elements.filter((ele) => !idMap[ele.id]);
  return elements
    .map((element) => {
      if (element.type === 'group') {
        const children = element.elements.filter((ele) => !idMap[ele.id]);
        const hit = children.length < element.elements.length;
        if (hit) {
          switch (children.length) {
            case 0:
              return null;
            case 1:
              return Object.assign({}, ungroup(Object.assign({}, element, { elements: children }))[0]);
            default:
              return regroup(Object.assign({}, element, { elements: children }));
          }
        }
        return element;
      }
      return element;
    })
    .filter((i) => i);
};

export const removeChildElements = (group, targetIdsMap) => {
  if (!group.elements.some((child) => targetIdsMap[child.id])) {
    return [group, []];
  }

  const children = ungroup(group);

  const [targetElements, nextChildren] = children.reduce(
    (res, child) => {
      res[+!targetIdsMap[child.id]].push(child);
      return res;
    },
    [[], []],
  );
  switch (nextChildren.length) {
    case 0:
      group = null;
      break;
    case 1:
      group = Object.assign({}, nextChildren[0]);
      break;
    default:
      group = makeGroup(nextChildren, {
        id: group.id,
        name: group.name,
      });
  }

  return [group, targetElements];
};

// 拖拽排序时，目标元素位置如果在选中元素内的话，就保留目标元素，插入的时候进行替换
export const removeChildElementsForSort = (group, targetIdsMap, targetId) => {
  if (!group.elements.some((child) => targetIdsMap[child.id])) {
    return [group, []];
  }
  const isTargetGroup = group.id === targetId || group.elements.find((i) => i.id === targetId);
  const children = ungroup(group);

  const [targetElements, nextChildren] = children.reduce(
    ([targets, children], child) => {
      if (targetIdsMap[child.id]) {
        targets.push(child);
      }
      if (!targetIdsMap[child.id] || targetId === child.id) {
        children.push(child);
      }
      return [targets, children];
    },
    [[], []],
  );
  switch (nextChildren.length) {
    case 0:
      group = isTargetGroup ? Object.assign({}, group, { elements: [] }) : null;
      break;
    case 1:
      group = isTargetGroup
        ? makeGroup(nextChildren, {
            id: group.id,
            name: group.name,
          })
        : Object.assign({}, nextChildren[0]);
      break;
    default:
      group = makeGroup(nextChildren, {
        id: group.id,
        name: group.name,
      });
  }

  return [group, targetElements];
};

export const addChildElements = (group, sourceElements, index = 0, replaceTarget = false) => {
  sourceElements = sourceElements.filter((child) => child.id !== group.id);
  if (!sourceElements?.length) {
    return group;
  }
  const children = ungroup(group);
  children.splice(
    index,
    replaceTarget ? 1 : 0,
    ...sourceElements.reduce((res, element) => {
      return res.concat(element.elements ? ungroup(element) : element);
    }, []),
  );

  return makeGroup(children, {
    id: group.id,
    name: group.name,
  });
};

export const sortElements = (sourceIds, targetElementId, elements, options = { asChildren: false }) => {
  const idMap = Object.fromEntries(sourceIds.map((id) => [id, true]));
  const targetInSource = idMap[targetElementId];
  let sourceElements = [];
  elements = elements
    .map((element) => {
      if (idMap[element.id]) {
        // 如果是群组id，则将其子元素添加到拖拽数组中，如果正好是目标数组，则保留群组对象，清空子元素
        if (element.elements?.find((child) => child.id === targetElementId)) {
          // 如果当前元素为拖拽中的群组元素，并且目标位置为自己的子元素，那对当前群组不提取任何元素
          return element;
        }
        sourceElements = sourceElements.concat(
          element.elements && targetElementId?.startsWith('elementGroup') ? ungroup(element) : element,
        );
        return element.id === targetElementId
          ? element.elements
            ? Object.assign({}, element, { elements: [] })
            : element
          : null;
      } else if (element.elements) {
        const [group, removedElements] = removeChildElementsForSort(element, idMap, targetElementId);
        sourceElements.push(...removedElements);
        return group;
      }
      return element;
    })
    .filter((i) => i);

  if (!targetElementId) {
    return elements.concat(sourceElements);
  } else {
    return elements.reduce((res, element) => {
      if (element.id === targetElementId && !options.asChildren) {
        res.push(...sourceElements.concat(targetInSource ? [] : element));
      } else if (element.id === targetElementId && options.asChildren) {
        res.push(addChildElements(element, sourceElements, element.elements.length));
      } else if (element.id !== targetElementId && element.elements) {
        const targetItemIndex = element.elements.findIndex((child) => child.id === targetElementId);
        res.push(
          targetItemIndex < 0 ? element : addChildElements(element, sourceElements, targetItemIndex, targetInSource),
        );
      } else {
        res.push(element);
      }
      return res;
    }, []);
  }
};

// 获取元素在panel中显示的x,y,scaleX,scaleY等，
// group内的要做额外计算
export const getElementXYWH = (element, group) => {
  let { x, y, width, height, scaleX, scaleY } = element;

  const props = { x, y, width, height, scaleX, scaleY };
  if (group) {
    Object.assign(props, {
      x: Math.round(group.x + element.x * group.scaleX),
      y: Math.round(group.y + element.y * group.scaleY),
      scaleX: element.scaleX * group.scaleX,
      scaleY: element.scaleY * group.scaleY,
    });
  } else {
    Object.assign(props, {
      x: Math.round(x),
      y: Math.round(y),
    });
  }
  return props;
};

// 获取元素在画布中渲染时的真实x,y,scaleX,scaleY等，
// group中元素需要减去group本身的位置（相对偏移量）
export const getElementRenderXYWH = (props, group, options = null) => {
  let invalid = false; // 宽高为0时无效
  const getXY = (key) => {
    if (isNaN(props[key])) return (invalid = true);
    return group ? (props[key] - group[key]) / group[`scale${key.toUpperCase()}`] : +props[key];
  };

  const getScaleXY = (key) => {
    if (isNaN(props[key]) || !+props[key]) return (invalid = true);
    let scaleValue = Math.abs(group ? props[key] / group[key] : props[key]);
    scaleValue = scaleValue / (options?.[{ scaleX: 'width', scaleY: 'height' }[key]] || 1);
    if (options?.lockRatio && options?.ratio) {
      switch (key) {
        case 'scaleX':
          props.scaleY = scaleValue / options.ratio;
          break;
        case 'scaleY':
          props.scaleX = scaleValue * options.ratio;
          break;
      }
    }
    return scaleValue;
  };
  const funMap = {
    x: () => getXY('x'),
    y: () => getXY('y'),
    scaleX: () => getScaleXY('scaleX'),
    scaleY: () => getScaleXY('scaleY'),
  };
  Object.keys(props).forEach((key) => {
    props[key] = funMap[key] ? funMap[key]() : props[key];
  });
  return invalid ? null : props;
};

export const calcMultiImageRect = (images) => {
  const position = { left: Infinity, right: -Infinity, top: Infinity, bottom: -Infinity };

  images.forEach((el) => {
    const { x: left, y: top, width, height, scaleX, scaleY } = el;

    const right = left + width * scaleX;
    const bottom = top + height * scaleY;
    if (scaleX < 0) {
      if (right < position.left) position.left = right;
      if (left > position.right) position.right = left;
    } else {
      if (left < position.left) position.left = left;
      if (right > position.right) position.right = right;
    }
    if (scaleY < 0) {
      if (bottom < position.top) position.top = top + bottom;
      if (top > position.bottom) position.bottom = top;
    } else {
      if (top < position.top) position.top = top;
      if (bottom > position.bottom) position.bottom = bottom;
    }
  });

  return {
    x: position.left,
    y: position.top,
    width: Math.round(position.right - position.left),
    height: Math.round(position.bottom - position.top),
  };
};

export const duplicateElements = () => {};
